/**
 * Utility to calculate earned check-in coins, check-in count,
 * and independent reward statuses (ZX vs Crypto)
 * ensuring exact consistency between EventParticipants, EventWinners, and Modals.
 */

export const calculateUserCoins = (
  userOrItem = {},
  matchedParticipant = {},
  selectedEvent = null,
  activeEventId = null
) => {
  const item = userOrItem || {};
  const participant = matchedParticipant || {};
  const userObj = item?.user || participant?.user || {};

  const eventCoinRate = Number(
    selectedEvent?.checkInCoin ??
      selectedEvent?.checkinCoin ??
      selectedEvent?.checkInPoints ??
      selectedEvent?.coin ??
      0
  );

  // Extract positive check-in count from any possible alias
  const checkinCandidate = [
    item.checkInCount,
    item.checkinCount,
    item.checkins,
    item.totalCheckins,
    item.totalCheckinCount,
    item.taskCount,
    item.tasks,
    item.totalTasks,
    item.checkInDays,
    item.daysCheckedIn,
    item.dailyCheckins,
    item.dailyCheckinCount,
    participant.checkInCount,
    participant.checkinCount,
    participant.checkins,
    participant.totalCheckins,
    participant.totalCheckinCount,
    participant.taskCount,
    participant.tasks,
    participant.totalTasks,
    participant.checkInDays,
    participant.daysCheckedIn,
    participant.dailyCheckins,
    participant.dailyCheckinCount,
  ].find((v) => v != null && !isNaN(Number(v)) && Number(v) > 0);

  const checkinCount = checkinCandidate != null ? Number(checkinCandidate) : 0;

  // Extract explicit coins if provided and > 0
  const explicitCoins = [
    item.checkInCoins,
    item.checkinCoins,
    item.coinsEarned,
    item.totalCoinsEarned,
    item.totalCoins,
    item.earnedCoins,
    item.coinEarned,
    item.checkInCoin,
    item.checkinCoin,
    item.checkInPoints,
    item.checkinPoints,
    item.points,
    item.score,
    item.totalScore,
    item.coins,
    item.coin,
    item.userCoins,
    item.userCoin,
    item.user?.coin,
    item.user?.coins,
    item.user?.checkInCoin,
    participant.checkInCoins,
    participant.checkinCoins,
    participant.coinsEarned,
    participant.totalCoinsEarned,
    participant.totalCoins,
    participant.earnedCoins,
    participant.coinEarned,
    participant.checkInCoin,
    participant.checkinCoin,
    participant.checkInPoints,
    participant.checkinPoints,
    participant.points,
    participant.score,
    participant.totalScore,
    participant.coins,
    participant.coin,
    participant.userCoins,
    participant.userCoin,
    participant.user?.coin,
    participant.user?.coins,
    participant.user?.checkInCoin,
    userObj.coin,
    userObj.coins,
    userObj.checkInCoin,
  ].find((v) => v != null && !isNaN(Number(v)) && Number(v) > 0);

  let rawEarnedCoins = 0;
  if (explicitCoins != null) {
    rawEarnedCoins = Number(explicitCoins);
  } else if (checkinCount > 0 && eventCoinRate > 0) {
    rawEarnedCoins = checkinCount * eventCoinRate;
  }

  // Check for reverted points
  const uid = String(item.userId || item.id || participant.userId || participant.id || userObj._id || userObj.id || "");
  const evtId = String(selectedEvent?.eventId || selectedEvent?.id || activeEventId || item.eventId || "");
  const localDeductions = getLocalPointDeductions();
  const deductionKey = `${evtId}_${uid}`;
  const localReverted =
    (deductionKey && localDeductions[deductionKey]?.revertedPoints) ||
    (uid && localDeductions[uid]?.revertedPoints) ||
    0;

  const explicitReverted = Number(item.revertedPoints || item.pointsReverted || participant.revertedPoints || 0);
  const totalReverted = Math.max(localReverted, explicitReverted);

  const earnedCoins = Math.max(0, rawEarnedCoins - totalReverted);

  return {
    earnedCoins,
    originalCoins: rawEarnedCoins,
    revertedPoints: totalReverted,
    checkinCount,
  };
};

/**
 * Builds a fast lookup map for participants by userId, id, username, email, and rank
 */
export const buildParticipantsMap = (participantsList = []) => {
  const map = {};
  if (!Array.isArray(participantsList)) return map;

  participantsList.forEach((p, idx) => {
    if (!p) return;
    const rank = p.userRank ?? p.rank ?? (idx + 1);
    const uid = p.userId ?? p.id ?? p.user?._id ?? p.user?.id;

    if (uid != null && uid !== "") {
      map[`uid_${uid}`] = p;
      map[`id_${uid}`] = p;
    }
    if (p.username) {
      map[`user_${String(p.username).toLowerCase().trim()}`] = p;
    }
    if (p.email) {
      map[`email_${String(p.email).toLowerCase().trim()}`] = p;
    }
    if (rank != null) {
      map[`rank_${rank}`] = p;
      map[`userRank_${rank}`] = p;
    }
  });

  return map;
};

/**
 * Finds matching participant from the participants lookup map
 */
export const findMatchingParticipant = (
  item,
  participantsMap = {},
  fallbackRank = null
) => {
  if (!item || !participantsMap) return {};
  const rank = item.userRank ?? item.rank ?? fallbackRank;
  const uid = item.userId ?? item.id ?? item.user?._id ?? item.user?.id;

  if (uid != null && uid !== "") {
    if (participantsMap[`uid_${uid}`]) return participantsMap[`uid_${uid}`];
    if (participantsMap[`id_${uid}`]) return participantsMap[`id_${uid}`];
  }
  if (item.username) {
    const key = `user_${String(item.username).toLowerCase().trim()}`;
    if (participantsMap[key]) return participantsMap[key];
  }
  if (item.email) {
    const key = `email_${String(item.email).toLowerCase().trim()}`;
    if (participantsMap[key]) return participantsMap[key];
  }
  if (rank != null) {
    if (participantsMap[`rank_${rank}`]) return participantsMap[`rank_${rank}`];
    if (participantsMap[`userRank_${rank}`]) return participantsMap[`userRank_${rank}`];
  }

  return {};
};

/**
 * Local storage persistence helper for point deductions / reversions
 */
const LOCAL_STORAGE_DEDUCTIONS_KEY = "zeta_event_point_deductions_v1";

export const getLocalPointDeductions = () => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_DEDUCTIONS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const setLocalPointDeduction = (eventId, userId, revertedPoints, data = {}) => {
  if (!userId) return;
  try {
    const all = getLocalPointDeductions();
    const key = eventId ? `${eventId}_${userId}` : String(userId);
    const existing = all[key]?.revertedPoints || 0;
    const newTotal = Number(existing) + Number(revertedPoints);

    all[key] = {
      ...(all[key] || {}),
      eventId,
      userId,
      revertedPoints: newTotal,
      lastRevertAmount: Number(revertedPoints),
      updatedAt: new Date().toISOString(),
      ...data,
    };
    localStorage.setItem(LOCAL_STORAGE_DEDUCTIONS_KEY, JSON.stringify(all));
  } catch (err) {
    console.warn("Failed to persist point deduction state", err);
  }
};

/**
 * Local storage persistence helper for independent approval tracking
 */
const LOCAL_STORAGE_APPROVALS_KEY = "zeta_winner_reward_approvals_v2";

export const getLocalRewardApprovals = () => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_APPROVALS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const setLocalRewardApproval = (historyId, data = {}) => {
  if (!historyId) return;
  try {
    const all = getLocalRewardApprovals();
    all[String(historyId)] = {
      ...(all[String(historyId)] || {}),
      ...data,
    };
    localStorage.setItem(LOCAL_STORAGE_APPROVALS_KEY, JSON.stringify(all));
  } catch (err) {
    console.warn("Failed to persist reward approval state", err);
  }
};


export const mergeHistoryWithLocalApprovals = (historyList = []) => {
  if (!Array.isArray(historyList)) return [];
  const localMap = getLocalRewardApprovals();

  return historyList.map((item) => {
    const hid = String(item.historyId || item._id || item.id || "");
    const local = (hid && localMap[hid]) || {};

    const merged = {
      ...item,
      ...local,
    };

    // Determine final txnId
    const backendTxn =
      item.txnId || item.transactionId || item.rewardCoin?.txnId || item.rewardCoin?.transactionId;
    if (backendTxn && String(backendTxn).trim() && String(backendTxn).trim() !== "null") {
      merged.txnId = String(backendTxn).trim();
      merged.transactionId = String(backendTxn).trim();
    } else if (local.txnId && String(local.txnId).trim()) {
      merged.txnId = String(local.txnId).trim();
      merged.transactionId = String(local.txnId).trim();
    }

    return merged;
  });
};

/**
 * Whether a leaderboard rank falls in a prize tier (e.g. "6-10", "11", "2-5").
 */
export const rankMatchesRange = (rank, rankRange) => {
  const r = Number(rank);
  if (!r || Number.isNaN(r)) return false;
  const range = String(rankRange || "").trim();
  if (!range) return false;
  const span = range.match(/^(\d+)\s*-\s*(\d+)$/);
  if (span) {
    const low = parseInt(span[1], 10);
    const high = parseInt(span[2], 10);
    return r >= low && r <= high;
  }
  if (/^\d+$/.test(range)) return r === parseInt(range, 10);
  return false;
};

export const getPrizeTierForRank = (rank, prizes = []) => {
  const r = Number(rank);
  if (!r || Number.isNaN(r) || !Array.isArray(prizes)) return null;
  for (const tier of prizes) {
    if (rankMatchesRange(r, tier.rankRange)) return tier;
  }
  return null;
};

/**
 * When winner history has no rewardZx/rewardCoin (e.g. prizes added after event ended),
 * fill amounts from the event prize configuration for that rank.
 */
export const enrichWinnerRewardFromEvent = (item = {}, event = null) => {
  if (!item || !event) return item;
  const rank = Number(item.userRank ?? item.rank);
  if (!rank || Number.isNaN(rank)) return item;

  const tier = getPrizeTierForRank(rank, event.prizes);
  if (!tier) return item;

  const existingZx = Number(item.rewardZx) || 0;
  const existingCoin = item.rewardCoin;
  const existingCoinAmt = Number(
    existingCoin?.coin ?? existingCoin?.coinAmount ?? existingCoin?.rewardUsdt ?? 0
  );

  const tierZx = Number(tier.rewardZx) || 0;
  const hasCoin =
    tier.hasCoinReward != null
      ? Boolean(tier.hasCoinReward)
      : Boolean(
          tier.coinSymbol ||
            Number(tier.coinAmount) > 0 ||
            Number(tier.rewardUsdt) > 0
        );
  const coinSymbol = tier.coinSymbol || "USDT";
  const coinAmount = Number(tier.coinAmount ?? tier.rewardUsdt ?? 0);

  const next = { ...item };
  if (existingZx <= 0 && tierZx > 0) next.rewardZx = tierZx;
  if (existingCoinAmt <= 0 && hasCoin && coinAmount > 0) {
    next.rewardCoin = {
      ...(existingCoin || {}),
      coin: coinAmount,
      coinAmount,
      coinSymbol,
    };
  }
  return next;
};

/**
 * Helper to determine independent approval statuses for ZX and Crypto rewards
 */
export const getItemRewardStatus = (item = {}) => {
  const zxAmount = Number(item.rewardZx) || 0;
  const hasCoinReward =
    item.rewardCoin &&
    (Number(item.rewardCoin.coin) > 0 || Number(item.rewardCoin.coinAmount) > 0);
  const coinAmount = Number(
    item.rewardCoin?.coin ?? item.rewardCoin?.coinAmount ?? 0
  );
  const coinSymbol =
    item.rewardCoin?.coinSymbol || item.rewardCoin?.symbol || "USDT";

  const txnId =
    (item.txnId && String(item.txnId).trim() && String(item.txnId).trim() !== "null" && String(item.txnId).trim() !== "undefined"
      ? String(item.txnId).trim()
      : "") ||
    (item.transactionId && String(item.transactionId).trim() && String(item.transactionId).trim() !== "null" && String(item.transactionId).trim() !== "undefined"
      ? String(item.transactionId).trim()
      : "") ||
    (item.rewardCoin?.txnId && String(item.rewardCoin.txnId).trim()
      ? String(item.rewardCoin.txnId).trim()
      : "") ||
    (item.rewardCoin?.transactionId && String(item.rewardCoin.transactionId).trim()
      ? String(item.rewardCoin.transactionId).trim()
      : "");

  const hasTxn = Boolean(txnId);
  const hasBothRewards = zxAmount > 0 && hasCoinReward && coinAmount > 0;

  // 1. Crypto Status
  let isCryptoCredited = false;
  if (hasCoinReward && coinAmount > 0) {
    if (item.cryptoStatus === "pending") {
      isCryptoCredited = false;
    } else if (item.cryptoStatus === "credited" || hasTxn) {
      isCryptoCredited = true;
    } else if (!hasBothRewards && item.rewardStatus === "credited") {
      // Winner ONLY has crypto reward (no ZX)
      isCryptoCredited = true;
    } else {
      // Winner has BOTH rewards and no explicit crypto approval or txnId
      isCryptoCredited = false;
    }
  }
  const isCryptoPending = hasCoinReward && coinAmount > 0 && !isCryptoCredited;

  // 2. ZX Status
  let isZxCredited = false;
  if (zxAmount > 0) {
    if (item.zxStatus === "pending") {
      isZxCredited = false;
    } else if (item.zxStatus === "credited") {
      isZxCredited = true;
    } else if (!hasBothRewards && item.rewardStatus === "credited") {
      // Winner ONLY has ZX reward (no crypto)
      isZxCredited = true;
    } else if (hasBothRewards && item.rewardStatus === "credited" && !hasTxn) {
      // If rewardStatus is credited and there is NO txnId, it means ZX was credited
      isZxCredited = true;
    } else {
      // If hasTxn is present but zxStatus was never credited, ZX remains pending
      isZxCredited = false;
    }
  }
  const isZxPending = zxAmount > 0 && !isZxCredited;

  // Fully credited when all applicable rewards are credited
  const isFullyCredited =
    (zxAmount > 0 ? isZxCredited : true) &&
    (hasCoinReward && coinAmount > 0 ? isCryptoCredited : true) &&
    (zxAmount > 0 || (hasCoinReward && coinAmount > 0));

  const hasAnyPending = isZxPending || isCryptoPending;

  return {
    zxAmount,
    hasCoinReward,
    coinAmount,
    coinSymbol,
    txnId,
    hasTxn,
    isZxCredited,
    isZxPending,
    isCryptoCredited,
    isCryptoPending,
    isFullyCredited,
    hasAnyPending,
  };
};

export const enrichHistoryListWithEvent = (
  list = [],
  event = null,
  page = 1,
  limit = 50
) => {
  if (!Array.isArray(list)) return [];
  return list.map((item, idx) => {
    const fallbackRank = (page - 1) * limit + idx + 1;
    const rank = Number(item.userRank ?? item.rank ?? fallbackRank);
    return enrichWinnerRewardFromEvent(
      { ...item, userRank: rank, rank },
      event
    );
  });
};

/** Extra fields for PATCH /event/history/:id/status when history rows lack stored amounts. */
export const buildApproveRewardPayload = (item = {}, extra = {}) => {
  const stats = getItemRewardStatus(item);
  const payload = { ...extra };
  if (stats.zxAmount > 0) payload.rewardZx = stats.zxAmount;
  if (stats.hasCoinReward && stats.coinAmount > 0) {
    payload.rewardCoin = {
      coin: stats.coinAmount,
      coinAmount: stats.coinAmount,
      coinSymbol: stats.coinSymbol,
    };
  }
  return payload;
};

/**
 * Determines the maximum winning rank (number of winners who qualify for rewards)
 * based on event prizes, maxWinners, winnerCount, or defaults to 500.
 */
export const getMaxWinningRank = (event = null) => {
  if (!event) return 500;
  if (event.winnerCount && Number(event.winnerCount) > 0) {
    return Number(event.winnerCount);
  }
  if (event.maxWinners && Number(event.maxWinners) > 0) {
    return Number(event.maxWinners);
  }
  if (
    event.totalWinners &&
    Number(event.totalWinners) > 0 &&
    Number(event.totalWinners) <= 1000
  ) {
    return Number(event.totalWinners);
  }

  if (Array.isArray(event.prizes) && event.prizes.length > 0) {
    let max = 0;
    event.prizes.forEach((p) => {
      const range = String(p.rankRange || "").trim();
      const match = range.match(/(\d+)\s*-\s*(\d+)/);
      if (match) {
        max = Math.max(max, parseInt(match[2], 10));
      } else if (/^\d+$/.test(range)) {
        max = Math.max(max, parseInt(range, 10));
      }
    });
    if (max > 0) return max;
  }

  return 500;
};

